import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRightIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
// 2026-07-04 tools-wide overhaul (operator-approved) : the SOLID
// CheckCircleIcon variant is the selected-state indicator on the
// service option cards below. Solid (filled) rather than outline so
// the selected card reads at a glance even on small screens - the
// outline set stays in use for the action icons above.
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import Alert from "../Alert";
import ActionButton from "../ActionButton";
import StartOverButton from "./StartOverButton";
import {
  ACME_SERVERS,
  DEFAULT_ACME_SERVER_ID,
} from "../../../shared/issuance/config";
import { parseConfigFile } from "../../issuance/configFile";

/**
 * Step 1 - Select Your Service.
 *
 * Customer picks one of the four allowed Automatic Certificate
 * Management Environment (ACME) endpoints and (optionally) drops a
 * previously-saved Trustico® Configuration File onto the drop zone to
 * reuse the saved ACME account at Step 2, or to pick an in-progress
 * issuance request back up from any browser or device.
 *
 * Post-pivot (2026-05-21) the service selection is a pure local
 * choice - no /api/issuance/* call is made for it. The selected
 * serverId and any loadedConfig is passed up to the page and
 * forwarded to Step 2, which creates the server-side session.
 *
 * onComplete({ serverId, serverUrl, label, loadedConfig? }).
 * onReset - the page-level Start Over handler (handleReset in
 * src/client/pages/Issuance.jsx). Operator directive 2026-07-05 :
 * every section carries its own centered Continue + Start Over row,
 * so the trigger moved from the page bottom into each step component ;
 * the handler and its discard/cleanup logic stay on the page.
 */
function Step1Service({ onComplete, onReset }) {
  const [selectedServerId, setSelectedServerId] = useState(
    DEFAULT_ACME_SERVER_ID
  );
  const [error, setError] = useState(null);
  const [loadedConfig, setLoadedConfig] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  // Loading a configuration file is a change of intent (2026-08-17,
  // the last member of the family review round 2 opened) : a customer
  // who armed Start Over and then dropped a saved request has plainly
  // chosen to resume it. Milder here than on Steps 3 and 4 - no
  // server-side request exists yet on this screen, so a stray press
  // only clears the form and the file is still on their disk - but it
  // is the same rule, and closing the family by construction is
  // cheaper than leaving one member to be found later.
  const [disarmToken, setDisarmToken] = useState(0);
  const disarmReset = useCallback(() => setDisarmToken((n) => n + 1), []);

  // Mounted guard - pattern-sweep partner to the one in ChallengePanel.
  // handleFile awaits file.text() which is fast but still async ; if
  // the customer drops a file and then unmounts the page (Start Over,
  // browser back) before the read finishes, setState on the resolved
  // promise would be a silent no-op, but the resolved Promise chain
  // still holds the closure. Guarding here keeps the pattern
  // consistent across every async-setState site in the Issuance Tool.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const selectedServer = ACME_SERVERS.find((s) => s.id === selectedServerId);

  const handleContinue = () => {
    if (!selectedServer) {
      // Customer-facing error messages are plain sentences (operator
      // ruling 2026-08-27 : UPPERCASE is for the logs) ; every tool
      // page follows the same rule.
      setError("Please select a service for your SSL Certificate.");
      return;
    }
    onComplete({
      serverId: selectedServer.id,
      serverUrl: selectedServer.url,
      label: selectedServer.label,
      loadedConfig,
    });
  };

  const handleFile = async (file) => {
    setError(null);
    if (!file) return;
    // Cap the drop at 1 Megabyte (MB) before reading the file into
    // memory. A valid Trustico® Configuration File is around 4-8
    // Kilobytes (KB) ; anything materially larger is either corrupted,
    // accidentally dropped (e.g. a customer drags a wrong file), or
    // intentionally oversized to consume browser memory. The server
    // body-parser caps at 1 MB too, so matching the limit here gives
    // the customer an immediate, clear rejection without making the
    // browser parse the file first.
    const MAX_CONFIG_FILE_BYTES = 1024 * 1024;
    if (file.size > MAX_CONFIG_FILE_BYTES) {
      setError(
        "The configuration file could not be loaded because it is larger than 1 megabyte."
      );
      return;
    }
    try {
      const text = await file.text();
      if (!mountedRef.current) return;
      const parsed = parseConfigFile(text);
      const matchingServer = ACME_SERVERS.find((s) => s.id === parsed.serverId);
      if (!matchingServer) {
        throw new Error(
          "The configuration file refers to an ACME service this tool does not offer (" +
            parsed.serverId +
            ")."
        );
      }
      setLoadedConfig(parsed);
      setSelectedServerId(matchingServer.id);
      disarmReset();
    } catch (e) {
      if (!mountedRef.current) return;
      // Customer-friendly errors from parseConfigFile / our throw
      // above are already sentences ; the prefix is one too, so the
      // Alert reads as two sentences. The fallback "Unknown error." preserves
      // the same posture when the underlying exception has no
      // message string.
      setError(
        "The configuration file could not be loaded. " +
          (e.message || "Unknown error.")
      );
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files && e.dataTransfer.files[0];
    if (file) void handleFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    // 2026-05-31 seventh-round code review I8 : the browser fires
    // dragover several times per second per pixel of pointer
    // travel. Calling setDragActive(true) unconditionally on every
    // event triggers a React re-render on every fire even though
    // the state did not change ; the equality guard collapses the
    // storm into one re-render per drag-enter / drag-leave cycle.
    if (!dragActive) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // 2026-07-03 PR #76 sixth-pass review (Finding 4d-i) : drag-leave
    // flicker fix. dragenter / dragleave bubble like mouseover /
    // mouseout, not like mouseenter / mouseleave, so moving the pointer
    // from the drop zone's own background onto one of its child
    // elements (the DocumentArrowDownIcon, the helper text, the browse
    // label) fires a dragleave on the container whose relatedTarget is
    // that child. The old code cleared dragActive on that false leave
    // and the following dragover set it true again - a visible border /
    // background flicker on every pass over a child. When the pointer is
    // still inside the drop zone (relatedTarget is a descendant of the
    // container) this is NOT a real exit : bail without touching state.
    // contains(null) returns false, so a genuine exit off the window
    // edge (relatedTarget === null) still clears correctly below.
    if (e.currentTarget.contains(e.relatedTarget)) {
      return;
    }
    // Symmetric equality guard to handleDragOver above - only fire
    // setState when the value actually changes.
    if (dragActive) {
      setDragActive(false);
    }
  };

  const handleFilePicker = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) void handleFile(file);
    e.target.value = "";
  };

  // Operator directive 2026-07-05 : on the PRISTINE Step 1 (nothing
  // chosen beyond the pre-selected default, no Configuration File
  // loaded, no error on screen) Start Over
  // renders DISABLED (grey) - there is nothing to start over FROM. The
  // moment the customer deviates from the defaults the button lights
  // up. The page-level state cannot see this component's local form
  // state, which is exactly why the pristine gate lives here.
  const pristine =
    selectedServerId === DEFAULT_ACME_SERVER_ID && !loadedConfig && !error;

  /**
   * Start Over on Step 1. The page-level onReset (handleReset in
   * src/client/pages/Issuance.jsx) resets page state, discards any
   * known server session, and wipes persistence - but it does NOT
   * remount this component (after a reset Step 1 stays the active
   * step), so the local form state must be cleared here or a loaded
   * Configuration File would survive the "fresh
   * start" the button promises. Local clears first, then the page
   * handler ; the handler itself is unchanged (operator directive
   * 2026-07-05 : only the trigger placement moves).
   */
  const handleStartOver = () => {
    setSelectedServerId(DEFAULT_ACME_SERVER_ID);
    setLoadedConfig(null);
    setError(null);
    // Guarded call, matching the onSessionCreated posture in
    // Step2Configure : parent-side bookkeeping must never crash the
    // local reset if a caller (e.g. an isolated component test)
    // renders without the prop.
    if (typeof onReset === "function") {
      onReset();
    }
  };

  return (
    <div className="space-y-6">
      {/*
        2026-07-04 Step 1 layout (operator direction, two rounds, after
        walking the deployed page : "It should be very clearly two
        column layout"). The previous single-column stack gave the
        service cards and the comeback options equal visual weight with
        no reading order. The panel now reads in priority order : the
        primary new-request flow (the service option cards and their
        Continue row, directly on the card surface since the 2026-08-13
        flattening) on top, then "Resume or Reissue with Your
        Configuration File" as the visually secondary section below it. The error Alert stays
        above everything, full width, so a failure from either section
        is impossible to miss.
      */}
      {error && (
        <Alert
          type="error"
          title="Unable to Continue"
          live
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/*
        2026-08-13 go-public layout flattening (operator-directed :
        "avoiding boxes in boxes or containers in containers") : this
        section previously drew its own rounded/bordered panel filled
        bg-white dark:bg-gray-800 - byte-identical to the StepPanel
        .card surface it sits on - and opened with an h4 + paragraph
        that restated the StepPanel heading and description rendered
        immediately above it. Both layers are dissolved : the card
        already provides the surface, border and padding, and the
        Step 1 StepPanel description prop (src/client/pages/Issuance.jsx)
        owns the explanation. The section element stays for the aria
        grouping (aria-labelledby, pointing at the visible heading
        described in the next comment), so the border-2 service option
        cards below are the ONLY boxes in the primary flow - the
        correct emphasis, since they are the thing being chosen.
      */}
      <section aria-labelledby="new-order-heading" className="space-y-4">
        {/*
          A VISIBLE heading and sentence on the primary path. The
          secondary path below ("Resume or Reissue with Your
          Configuration File") had both,
          while the four service cards had only an invisible
          aria-label, so the page gave the comeback path more
          prominence than the path almost every customer takes.
        */}
        <div>
          <h3
            id="new-order-heading"
            className="text-base font-semibold text-heading mb-2"
          >
            Start Another Issuance Request
          </h3>
          <p className="text-body">
            Choose the service that matches the SSL Certificate license.
          </p>
        </div>
        {/*
            2026-05-31 seventh-round code review nit-6 : the radios
            live inside a <label> wrapper for click-anywhere
            ergonomics, BUT the wrapping label has no htmlFor/id
            pair so screen readers may not group the radio with its
            visible text. Step 2 uses the explicit htmlFor + id
            pattern. Adding id and htmlFor here gives both pointer
            AND assistive-tech users the right association. The
            click-anywhere ergonomics survive because the input is
            still nested inside the label.
          */}
        {/*
            2026-07-04 tools-wide overhaul (operator-approved) : the service
            selector is redesigned from a small radio list into large
            clickable option cards, per the operator's direction ("each
            option should look like a big button/option with a proper design
            and spacing"). The nit-6 accessibility contract above is
            PRESERVED, not replaced : each card is still a <label htmlFor>
            wrapping a REAL <input type="radio">. The input is sr-only (the
            same visually-hidden-but-focusable posture as the file-picker
            input below, seventh-round I9), so it keeps its place in the tab
            order, keeps the native radio-group arrow-key semantics, and
            keeps its accessible name from the wrapping label's content. The
            FULL card is the pointer click target because the input is
            nested inside the label. Selected state = blue border + subtle
            blue fill + a solid CheckCircleIcon indicator ; keyboard focus
            is surfaced on the visible card via the Tailwind
            peer-focus-visible ring, because the sr-only input itself has no
            visible box for the browser's native focus outline to land on.
          */}
        <div
          role="radiogroup"
          aria-labelledby="new-order-heading"
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
        >
          {ACME_SERVERS.map((server) => {
            const radioId = "acme-server-" + server.id;
            const isSelected = selectedServerId === server.id;
            return (
              <label
                key={server.id}
                htmlFor={radioId}
                className="relative block cursor-pointer"
              >
                <input
                  id={radioId}
                  type="radio"
                  name="acme-server"
                  value={server.id}
                  checked={isSelected}
                  onChange={(e) => setSelectedServerId(e.target.value)}
                  className="sr-only peer"
                />
                {/*
                h-full keeps the two cards in a desktop grid row the
                same height when one label wraps to a second line, so
                the borders align instead of leaving a ragged edge.
              */}
                <div
                  className={`rounded-lg border-2 p-4 h-full transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 peer-focus-visible:ring-offset-2 dark:peer-focus-visible:ring-offset-gray-800 ${
                    isSelected
                      ? "border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/30"
                      : "border-control bg-white dark:bg-gray-800 hover:border-blue-400 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="font-semibold text-heading leading-snug">
                      {server.label}
                    </span>
                    {/*
                    aria-hidden : the check is a purely visual duplicate
                    of the radio's checked state, which assistive
                    technology already announces from the input itself.
                    The data-testid gives the component test a stable
                    hook for asserting which card renders as selected.
                  */}
                    {isSelected && (
                      <CheckCircleIcon
                        data-testid={"acme-server-selected-" + server.id}
                        aria-hidden="true"
                        className="w-6 h-6 text-link shrink-0"
                      />
                    )}
                  </div>
                  {/* Plain-language description (2026-08-13 go-public
                      content ruling) : the cards previously
                      distinguished themselves only by the raw
                      directory URL below - meaningless to a
                      non-expert choosing which service their order
                      belongs to. Sourced from the shared ACME_SERVERS
                      entry so the four descriptions stay single-source
                      with the service list. */}
                  <p className="text-body mt-2">{server.description}</p>
                  <p className="text-muted mt-2 font-mono break-all">
                    {server.url}
                  </p>
                </div>
              </label>
            );
          })}
        </div>
      </section>

      <section
        aria-labelledby="resume-order-heading"
        // No fill and no border : this section renders inside
        // StepPanel's card, and the tint it used to carry made a
        // second surface with the two option cards - and the dashed
        // drop zone inside one of them - nested three and four deep.
        // Separation from the primary path is whitespace alone.
        //
        // That whitespace is pt-2 ON TOP OF the parent's space-y-6,
        // not a margin : Tailwind's space utility emits
        // `.space-y-6 > :not([hidden]) ~ :not([hidden])`, specificity
        // (0,3,0), so an mt-8 here (0,1,0) never rendered whatever the
        // class order. 24px inherited plus 8px padding is the 32px
        // major-group gap the Design Constitution asks for.
        className="pt-2 space-y-4"
      >
        <div>
          {/*
              h3 text-base (2026-08-13 flattening) : in-panel
              sub-headings step DOWN from the StepPanel h2's text-lg
              instead of matching it, and h3 removes the h2->h4 outline
              level skip flagged in the go-public audit.
            */}
          <h3
            id="resume-order-heading"
            className="text-base font-semibold text-heading"
          >
            Resume or Reissue with Your Configuration File
          </h3>
          <p className="text-body mt-2">
            Already started an issuance request, or need a reissue? Upload or
            drop a Trustico® configuration file you saved earlier to continue
            that request where it left off, or to reissue your SSL Certificate
            using the same license without entering your credentials again.
          </p>
        </div>

        {/*
          NO card around the drop zone (operator directive 2026-08-16 :
          "there is only one option now"). While there were two ways
          back in, each was a bordered card with its own heading and
          sentence so the pair read as a choice. With one way back, that
          card put a second heading and a second paragraph inside a box
          that already sat under the section's own heading and
          paragraph - boxes in boxes saying the same thing twice. The
          section now matches "Start Another Issuance Request" directly
          above it : heading, one description, then the content.
        */}
        {/*
              The dashed border survives the 2026-08-13 frame
              flattening deliberately : unlike the dissolved decorative
              panels it is the universal drop-target affordance - it
              MEANS "a file can be dropped here" rather than merely
              grouping content, and a borderless zone would be
              undiscoverable.
            */}
        <div
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`rounded-md border-2 border-dashed transition-colors p-4 text-center ${
            dragActive
              ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30"
              : "border-control"
          }`}
        >
          <DocumentArrowDownIcon className="mx-auto w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
          {loadedConfig ? (
            <div className="text-body">
              <p className="font-semibold text-heading">
                Loaded Trustico® Configuration File
              </p>
              <p className="font-mono mt-1 break-all">
                ACME Account : {loadedConfig.accountUrl}
              </p>
              <p className="mt-1 text-muted break-all">
                Domain Names : {loadedConfig.domains.join(", ")}
              </p>
              <button
                type="button"
                onClick={() => setLoadedConfig(null)}
                className="mt-3 text-link underline"
              >
                Clear Loaded Configuration
              </button>
            </div>
          ) : (
            <div className="text-body">
              {/*
                    2026-07-04 operator direction (second layout round) :
                    the drop zone's two-line prose ("Drop a ... Here" +
                    a small trailing "browse for the file" link) becomes
                    ONE Title-Case linked line - "Upload or Drop
                    Configuration File Here" - and the WHOLE line is the
                    file-picker link ("which should just be the link
                    perhaps"). Dropping onto the zone still works via
                    the container's drag handlers ; clicking anywhere on
                    the line opens the picker because the label wraps
                    the input.
                  */}
              {/*
                    2026-05-31 seventh-round code review I9 : the
                    file input must remain reachable by keyboard
                    tab order so customers using assistive
                    technology can pick a file. `className="hidden"`
                    (display:none) drops the element out of the tab
                    order entirely. The Tailwind `sr-only` utility
                    visually hides the input but leaves it focusable
                    and in the accessibility tree ; the surrounding
                    <label> still triggers the picker on click for
                    pointer-based users.
                  */}
              {/*
                    2026-07-03 PR #76 sixth-pass review (Finding 4d-ii) :
                    dropped the redundant ".trustico-config.json" accept
                    token. Trustico® Configuration Files are exported with
                    extension CONFIG_FILE_EXTENSION ("trustico-config.json",
                    see src/shared/issuance/config.js), so every one of them ends
                    in ".json" and is already matched by the ".json" token.
                    ".trustico-config.json" matched a strict subset of what
                    ".json" already accepts, so removing it leaves the set
                    of pickable files identical while dropping the
                    duplicate.
                  */}
              <label className="font-medium text-link underline cursor-pointer">
                Upload or Drop Configuration File Here
                <input
                  type="file"
                  accept=".json"
                  className="sr-only"
                  onChange={handleFilePicker}
                />
              </label>
            </div>
          )}
        </div>
      </section>
      {/*
      2026-07-04 operator consistency directive ("buttons should
      be standard Continue and Start Over") : every
      step-progression primary button on the Issuance Tool is
      labelled exactly "Continue" - the step context comes from
      the surrounding panel heading, not from a per-step button
      label. This particular Continue stays ENABLED at all
      times because a service is always pre-selected
      (DEFAULT_ACME_SERVER_ID), unlike the
      disabled-until-complete gates on Step 2 and Step 3.

      Operator directive 2026-07-05 ("the Continue button in the
      first section is not centered like all the other buttons...
      Always there should be a Continue and Start Over button in
      EACH section") : this was the page's one left-stuck primary
      button. 2026-08-13 go-public flattening : the centered row
      now uses the equal-width two-column grid every other tool
      page's paired action row shares (the earlier content-sized
      flex row rendered the two buttons at visibly different
      widths). Start Over is disabled on the pristine panel (see
      the `pristine` gate above).
      */}
      <div className="flex justify-center">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full max-w-2xl">
          <ActionButton
            onClick={handleContinue}
            icon={ArrowRightIcon}
            variant="primary"
          >
            Continue
          </ActionButton>
          <StartOverButton
            onReset={handleStartOver}
            disabled={pristine}
            disarmToken={disarmToken}
          />
        </div>
      </div>
    </div>
  );
}

export default Step1Service;
